import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Eye, Settings, History, X, Save, Clock, Calendar } from 'lucide-react'
import { newsApi } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import { News, NewsImageHistory, NewsCarouselVisibility, NewsRoleVisibility, UserType } from '../../types'
import NewsImage from '../../components/NewsImage'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

const AdminNews = () => {
  const [news, setNews] = useState<News[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNews, setSelectedNews] = useState<News | null>(null)
  const [currentDateTime, setCurrentDateTime] = useState(new Date())
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showImageHistoryModal, setShowImageHistoryModal] = useState(false)
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)
  const [showCarouselVisibilityModal, setShowCarouselVisibilityModal] = useState(false)
  const [imageHistory, setImageHistory] = useState<NewsImageHistory[]>([])
  const [carouselVisibility, setCarouselVisibility] = useState<NewsCarouselVisibility[]>([])
  const [roleVisibility, setRoleVisibility] = useState<NewsRoleVisibility[]>([])
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 0,
    link_url: '',
    link_text: '',
    expires_at: '',
    is_active: true,
    image: null as File | null
  })

  const roles: UserType[] = ['customer', 'store_owner', 'store_admin', 'dispatcher', 'chef', 'delivery', 'platform_admin']

  useEffect(() => {
    loadNews()
    loadCarouselVisibility()
  }, [])

  // Actualizar fecha y hora cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const loadNews = async () => {
    const cacheKey = 'admin_news_all'
    
    // Intentar cargar desde cache primero
    const cachedNews = getCacheStale<News[]>(cacheKey)
    if (cachedNews && cachedNews.length > 0) {
      setNews(cachedNews)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await newsApi.getAllNews(100, 0)
      if (response.success) {
        const newsData = response.data || []
        setNews(newsData)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, newsData, 5 * 60 * 1000)
      }
    } catch (error: any) {
      // Si es un 404 (endpoint no existe en el backend), no mostrar error
      // El interceptor de Axios ya marca estos endpoints como silenciosos
      if (error.name === 'Silent404Error' || error.response?.status === 404) {
        // Endpoint no implementado en el backend, simplemente dejar la lista vacía
        setNews([])
        return
      }
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedNews) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Para otros errores, mostrar mensaje
      console.error('Error loading news:', error)
      toast.error('Error al cargar noticias')
    } finally {
      setLoading(false)
    }
  }

  const loadCarouselVisibility = async () => {
    const cacheKey = 'admin_news_carousel_visibility'
    
    // Intentar cargar desde cache primero
    const cachedVisibility = getCacheStale<NewsCarouselVisibility[]>(cacheKey)
    if (cachedVisibility && cachedVisibility.length > 0) {
      setCarouselVisibility(cachedVisibility)
    }

    try {
      const response = await newsApi.getCarouselVisibility()
      if (response.success) {
        const visibilityData = response.data || []
        setCarouselVisibility(visibilityData)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, visibilityData, 5 * 60 * 1000)
      }
    } catch (error: any) {
      // Si es un 404 (endpoint no existe en el backend), no mostrar error
      if (error.name === 'Silent404Error' || error.response?.status === 404) {
        // Endpoint no implementado en el backend, simplemente dejar la lista vacía
        setCarouselVisibility([])
        return
      }
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedVisibility) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Para otros errores, loguear pero no mostrar toast (es opcional)
      console.error('Error loading carousel visibility:', error)
    }
  }

  const loadImageHistory = async (newsId: string) => {
    const cacheKey = `admin_news_image_history_${newsId}`
    
    // Intentar cargar desde cache primero
    const cachedHistory = getCacheStale<NewsImageHistory[]>(cacheKey)
    if (cachedHistory && cachedHistory.length > 0) {
      setImageHistory(cachedHistory)
    }

    try {
      const response = await newsApi.getNewsImageHistory(newsId)
      if (response.success) {
        const historyData = response.data || []
        setImageHistory(historyData)
        
        // Guardar en cache (10 minutos de TTL - historial cambia poco)
        setCache(cacheKey, historyData, 10 * 60 * 1000)
      }
    } catch (error: any) {
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedHistory) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      console.error('Error loading image history:', error)
    }
  }

  const loadRoleVisibility = async (newsId: string) => {
    const cacheKey = `admin_news_role_visibility_${newsId}`
    
    // Intentar cargar desde cache primero
    const cachedVisibility = getCacheStale<NewsRoleVisibility[]>(cacheKey)
    if (cachedVisibility && cachedVisibility.length > 0) {
      setRoleVisibility(cachedVisibility)
    }

    try {
      const response = await newsApi.getNewsRoleVisibility(newsId)
      if (response.success) {
        const visibilityData = response.data || []
        setRoleVisibility(visibilityData)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, visibilityData, 5 * 60 * 1000)
      }
    } catch (error: any) {
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedVisibility) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      console.error('Error loading role visibility:', error)
    }
  }

  const handleCreateNews = async () => {
    try {
      const formDataToSend = new FormData()
      formDataToSend.append('title', formData.title)
      formDataToSend.append('content', formData.content)
      formDataToSend.append('priority', formData.priority.toString())
      if (formData.link_url) formDataToSend.append('link_url', formData.link_url)
      if (formData.link_text) formDataToSend.append('link_text', formData.link_text)
      if (formData.expires_at) formDataToSend.append('expires_at', formData.expires_at)
      formDataToSend.append('is_active', formData.is_active.toString())
      if (formData.image) formDataToSend.append('image', formData.image)

      const response = await newsApi.createNews(formDataToSend)
      if (response.success) {
        toast.success('Noticia creada exitosamente')
        setShowCreateModal(false)
        resetForm()
        await loadNews()
      }
    } catch (error: any) {
      console.error('Error creating news:', error)
      toast.error(error.response?.data?.message || 'Error al crear noticia')
    }
  }

  const handleUpdateNews = async () => {
    if (!selectedNews) return

    try {
      const formDataToSend = new FormData()
      if (formData.title) formDataToSend.append('title', formData.title)
      if (formData.content) formDataToSend.append('content', formData.content)
      formDataToSend.append('priority', formData.priority.toString())
      if (formData.link_url !== undefined) formDataToSend.append('link_url', formData.link_url || '')
      if (formData.link_text !== undefined) formDataToSend.append('link_text', formData.link_text || '')
      if (formData.expires_at !== undefined) formDataToSend.append('expires_at', formData.expires_at || '')
      formDataToSend.append('is_active', formData.is_active.toString())
      if (formData.image) formDataToSend.append('image', formData.image)

      const response = await newsApi.updateNews(selectedNews.id, formDataToSend)
      if (response.success) {
        toast.success('Noticia actualizada exitosamente')
        setShowEditModal(false)
        resetForm()
        await loadNews()
      }
    } catch (error: any) {
      console.error('Error updating news:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar noticia')
    }
  }

  const handleDeleteNews = async () => {
    if (!selectedNews) return

    try {
      const response = await newsApi.deleteNews(selectedNews.id)
      if (response.success) {
        toast.success('Noticia eliminada exitosamente')
        setShowDeleteModal(false)
        await loadNews()
      }
    } catch (error: any) {
      console.error('Error deleting news:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar noticia')
    }
  }

  const handleUpdateCarouselVisibility = async (role: string, isVisible: boolean) => {
    try {
      const response = await newsApi.updateCarouselVisibility(role, isVisible)
      if (response.success) {
        toast.success('Visibilidad del carousel actualizada')
        await loadCarouselVisibility()
      }
    } catch (error: any) {
      console.error('Error updating carousel visibility:', error)
      toast.error('Error al actualizar visibilidad del carousel')
    }
  }

  const handleUpdateRoleVisibility = async () => {
    if (!selectedNews) return

    try {
      const rolesToUpdate = roles.map(role => {
        const existing = roleVisibility.find(rv => rv.role === role)
        return {
          role,
          is_visible: existing ? existing.is_visible : true
        }
      })

      const response = await newsApi.updateNewsRoleVisibility(selectedNews.id, rolesToUpdate)
      if (response.success) {
        toast.success('Visibilidad por rol actualizada')
        setShowVisibilityModal(false)
        await loadRoleVisibility(selectedNews.id)
      }
    } catch (error: any) {
      console.error('Error updating role visibility:', error)
      toast.error('Error al actualizar visibilidad por rol')
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 0,
      link_url: '',
      link_text: '',
      expires_at: '',
      is_active: true,
      image: null
    })
    setSelectedNews(null)
  }

  const openEditModal = (newsItem: News) => {
    setSelectedNews(newsItem)
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      priority: newsItem.priority,
      link_url: newsItem.link_url || '',
      link_text: newsItem.link_text || '',
      expires_at: newsItem.expires_at ? new Date(newsItem.expires_at).toISOString().slice(0, 16) : '',
      is_active: newsItem.is_active,
      image: null
    })
    setShowEditModal(true)
  }

  const openImageHistoryModal = async (newsItem: News) => {
    setSelectedNews(newsItem)
    await loadImageHistory(newsItem.id)
    setShowImageHistoryModal(true)
  }

  const openVisibilityModal = async (newsItem: News) => {
    setSelectedNews(newsItem)
    await loadRoleVisibility(newsItem.id)
    setShowVisibilityModal(true)
  }

  // Función para calcular el tiempo restante hasta expires_at
  const getTimeRemaining = (expiresAt?: string): string | null => {
    if (!expiresAt) return null
    
    const now = currentDateTime
    const expires = new Date(expiresAt)
    const diff = expires.getTime() - now.getTime()
    
    if (diff <= 0) return 'Expirada'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }

  // Función para formatear fecha y hora
  const formatDateTime = (date: Date): string => {
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <AdminNavHeader title="Gestión de Noticias" subtitle="Administra las noticias del carousel de inicio" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Fecha y hora actual */}
        <div className="mb-6 flex items-center justify-end">
          <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl border border-gray-200 shadow-sm">
            <Calendar className="w-5 h-5 text-primary-600" />
            <div className="text-sm">
              <div className="font-semibold text-gray-900">{formatDateTime(currentDateTime)}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-6">
          <div className="hidden">
            {/* Título oculto porque AdminNavHeader ya lo muestra */}
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCarouselVisibilityModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all flex items-center gap-2"
            >
              <Settings className="w-5 h-5" />
              Visibilidad del Carousel
            </button>
            <button
              onClick={() => {
                resetForm()
                setShowCreateModal(true)
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nueva Noticia
            </button>
          </div>
        </div>

        {/* News List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
            <p className="text-gray-500">No hay noticias creadas</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {news.map((newsItem) => (
              <div
                key={newsItem.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  {newsItem.image_url && (
                    <div className="w-32 h-32 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                      <NewsImage
                        imageKey={newsItem.image_url}
                        className="w-full h-full object-cover"
                        alt={newsItem.title}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{newsItem.title}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">{newsItem.content}</p>
                        {/* Información de fechas y tiempo restante */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-2">
                          {newsItem.created_at && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>
                                Creada: {new Date(newsItem.created_at).toLocaleString('es-ES', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          )}
                          {newsItem.expires_at && (
                            <div className={`flex items-center gap-1 font-semibold ${
                              getTimeRemaining(newsItem.expires_at) === 'Expirada' 
                                ? 'text-red-600' 
                                : 'text-amber-600'
                            }`}>
                              <Clock className="w-3 h-3" />
                              <span>
                                {getTimeRemaining(newsItem.expires_at) === 'Expirada' 
                                  ? 'Expirada' 
                                  : `Expira en: ${getTimeRemaining(newsItem.expires_at)}`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        {newsItem.is_active ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                            Activa
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">
                            Inactiva
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <button
                        onClick={() => openEditModal(newsItem)}
                        className="px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-100 transition-all flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => openImageHistoryModal(newsItem)}
                        className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-all flex items-center gap-2"
                      >
                        <History className="w-4 h-4" />
                        Historial
                      </button>
                      <button
                        onClick={() => openVisibilityModal(newsItem)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-all flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Visibilidad
                      </button>
                      <button
                        onClick={() => {
                          setSelectedNews(newsItem)
                          setShowDeleteModal(true)
                        }}
                        className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-all flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Nueva Noticia</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contenido</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Expiración</label>
                    <input
                      type="datetime-local"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL de Enlace</label>
                    <input
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Texto del Enlace</label>
                    <input
                      type="text"
                      value={formData.link_text}
                      onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagen</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active_create"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="is_active_create" className="text-sm font-medium text-gray-700">
                    Noticia activa
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleCreateNews}
                    className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Crear Noticia
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateModal(false)
                      resetForm()
                    }}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal - Similar structure to Create Modal */}
        {showEditModal && selectedNews && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Editar Noticia</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false)
                    resetForm()
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Same form fields as create modal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contenido</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Expiración</label>
                    <input
                      type="datetime-local"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL de Enlace</label>
                    <input
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Texto del Enlace</label>
                    <input
                      type="text"
                      value={formData.link_text}
                      onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Imagen (opcional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active_edit"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="is_active_edit" className="text-sm font-medium text-gray-700">
                    Noticia activa
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleUpdateNews}
                    className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Guardar Cambios
                  </button>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      resetForm()
                    }}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedNews && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eliminar Noticia</h2>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que deseas eliminar la noticia "{selectedNews.title}"? Esta acción no se puede deshacer.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteNews}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all"
                >
                  Eliminar
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setSelectedNews(null)
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image History Modal */}
        {showImageHistoryModal && selectedNews && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Historial de Imágenes</h2>
                <button
                  onClick={() => {
                    setShowImageHistoryModal(false)
                    setSelectedNews(null)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {imageHistory.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay historial de imágenes</p>
                ) : (
                  <div className="space-y-4">
                    {imageHistory.map((historyItem) => (
                      <div
                        key={historyItem.id}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                      >
                        <NewsImage
                          imageKey={historyItem.image_url}
                          className="w-24 h-24 object-cover rounded-lg"
                          alt="Historial"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">
                            {historyItem.changed_by_name || 'Usuario desconocido'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(historyItem.changed_at).toLocaleString()}
                          </p>
                          {historyItem.is_current && (
                            <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                              Actual
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Role Visibility Modal */}
        {showVisibilityModal && selectedNews && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Visibilidad por Rol</h2>
                <button
                  onClick={() => {
                    setShowVisibilityModal(false)
                    setSelectedNews(null)
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">Selecciona qué roles pueden ver esta noticia:</p>
                <div className="space-y-3">
                  {roles.map((role) => {
                    const existing = roleVisibility.find(rv => rv.role === role)
                    const isVisible = existing ? existing.is_visible : true
                    return (
                      <div key={role} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="font-medium text-gray-900 capitalize">{role.replace('_', ' ')}</span>
                        <button
                          onClick={() => {
                            const updated = roleVisibility.map(rv =>
                              rv.role === role ? { ...rv, is_visible: !rv.is_visible } : rv
                            )
                            if (!existing) {
                              updated.push({
                                id: '',
                                news_id: selectedNews.id,
                                role,
                                is_visible: true,
                                created_at: ''
                              })
                            }
                            setRoleVisibility(updated)
                          }}
                          className={`w-12 h-6 rounded-full transition-all ${
                            isVisible ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`block w-5 h-5 bg-white rounded-full transform transition-transform ${
                              isVisible ? 'translate-x-6' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleUpdateRoleVisibility}
                    className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-all"
                  >
                    Guardar Cambios
                  </button>
                  <button
                    onClick={() => {
                      setShowVisibilityModal(false)
                      setSelectedNews(null)
                    }}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Carousel Visibility Modal */}
        {showCarouselVisibilityModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Visibilidad del Carousel</h2>
                <button
                  onClick={() => setShowCarouselVisibilityModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-4">Selecciona qué roles pueden ver el carousel de noticias:</p>
                <div className="space-y-3">
                  {roles.map((role) => {
                    const existing = carouselVisibility.find(cv => cv.role === role)
                    const isVisible = existing ? existing.is_visible : true
                    return (
                      <div key={role} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <span className="font-medium text-gray-900 capitalize">{role.replace('_', ' ')}</span>
                        <button
                          onClick={() => handleUpdateCarouselVisibility(role, !isVisible)}
                          className={`w-12 h-6 rounded-full transition-all ${
                            isVisible ? 'bg-primary-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`block w-5 h-5 bg-white rounded-full transform transition-transform ${
                              isVisible ? 'translate-x-6' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminNews

